import { refreshToken } from '@/services/api';

type SessionEvent =
  | 'refresh-success'
  | 'refresh-failure'
  | 'reauth-required'
  | 'reauth-success'
  | 'reauth-cancelled';

type SessionEventHandler = (detail?: unknown) => void;

type RefreshBroadcastMessage = {
  type: SessionEvent;
  expiresAt?: number | null;
  owner?: string;
};

const CHANNEL_NAME = 'session-refresh';
const LOCK_KEY = 'session_refresh_lock';
const LOCK_TTL_MS = 15000;
const REFRESH_WAIT_TIMEOUT_MS = 20000;
const ACTIVE_WINDOW_MS = 5 * 60 * 1000;
const REFRESH_WINDOW_MS = 60 * 1000;
const AUTO_REFRESH_INTERVAL_MS = 3 * 60 * 1000;
const FALLBACK_ACCESS_TTL_MS = 5 * 60 * 1000;

export class SessionManager {
  private readonly tabId: string;
  private readonly eventTarget = new EventTarget();
  private readonly isDebug = !(
    (import.meta as unknown as { env?: { PROD?: boolean } }).env?.PROD
  );
  private channel: BroadcastChannel | null = null;
  private expiresAt: number | null = null;
  private lastRefreshAt = 0;
  private lastActivityAt = Date.now();
  private refreshPromise: Promise<void> | null = null;
  private reauthRequired = false;
  private reauthPromise: Promise<void> | null = null;
  private resolveReauth: (() => void) | null = null;
  private rejectReauth: ((error: Error) => void) | null = null;
  private activityTimer: number | null = null;
  private activityCheckTimer: number | null = null;
  private isTracking = false;
  private isAuthenticated = false;
  private ongoingOperations = 0;

  constructor() {
    this.tabId = SessionManager.generateTabId();
    this.setupBroadcastChannel();
  }

  setAuthenticated(isAuthenticated: boolean) {
    const wasAuthenticated = this.isAuthenticated;
    this.isAuthenticated = isAuthenticated;

    // Prevent an immediate refresh on first activity right after login.
    // Without this, `lastRefreshAt = 0` makes `checkForRefresh()` call refresh
    // on the first activity event, which can race cookie propagation and
    // incorrectly force the app into reauth mode.
    if (isAuthenticated && !wasAuthenticated) {
      this.lastRefreshAt = Date.now();
      this.lastActivityAt = Date.now();
    }
    if (!isAuthenticated && !this.reauthRequired) {
      this.resetState();
    }
  }

  start() {
    if (this.isTracking) return;
    this.isTracking = true;
    this.attachActivityListeners();
    this.activityCheckTimer = window.setInterval(() => {
      this.checkForRefresh();
    }, 30000);
  }

  stop() {
    this.isTracking = false;
    this.detachActivityListeners();
    if (this.activityTimer) {
      window.clearTimeout(this.activityTimer);
      this.activityTimer = null;
    }
    if (this.activityCheckTimer) {
      window.clearInterval(this.activityCheckTimer);
      this.activityCheckTimer = null;
    }
  }

  on(event: SessionEvent, handler: SessionEventHandler) {
    const wrapped = (evt: Event) => {
      handler((evt as CustomEvent).detail);
    };
    this.eventTarget.addEventListener(event, wrapped);
    return () => this.eventTarget.removeEventListener(event, wrapped);
  }

  async ensureValidSession(reason?: string) {
    if (this.reauthRequired) {
      if (this.isDebug) {
        console.info('[SessionManager] Awaiting reauth', { reason });
      }
      await this.waitForReauth();
      return;
    }
    if (!this.isAuthenticated && !reason) {
      throw new Error('Session refresh skipped: not authenticated');
    }
    await this.refresh(reason);
  }

  triggerReauth(reason?: string) {
    this.requireReauth(new Error(reason || 'Reauthentication required'));
  }

  notifyReauthSuccess() {
    this.reauthRequired = false;
    if (this.resolveReauth) {
      this.resolveReauth();
    }
    this.clearReauthPromise();
    this.emit('reauth-success');
    this.broadcast({ type: 'reauth-success' });
    if (this.isDebug) {
      console.info('[SessionManager] Reauth success');
    }
  }

  notifyReauthCancelled() {
    const error = new Error('Reauthentication cancelled');
    if (this.rejectReauth) {
      this.rejectReauth(error);
    }
    this.clearReauthPromise();
    this.emit('reauth-cancelled');
    this.broadcast({ type: 'reauth-cancelled' });
    if (this.isDebug) {
      console.warn('[SessionManager] Reauth cancelled');
    }
  }

  isReauthRequired() {
    return this.reauthRequired;
  }

  /**
   * Signals a point-in-time activity to reset the idle timer.
   * For long-running operations, use startOperation()/endOperation() instead.
   */
  markActive() {
    this.lastActivityAt = Date.now();
  }

  /**
   * Indicates that a long-running operation (e.g., file upload) has started.
   * The session will be kept alive even without user activity until endOperation() is called.
   */
  startOperation() {
    this.ongoingOperations++;
  }

  /**
   * Indicates that a long-running operation has completed.
   */
  endOperation() {
    this.ongoingOperations = Math.max(0, this.ongoingOperations - 1);
  }

  /**
   * Returns true if there are any ongoing operations (e.g., uploads in progress).
   */
  hasOngoingOperations() {
    return this.ongoingOperations > 0;
  }

  /**
   * Returns true if we successfully refreshed the token within the given window.
   * Used to skip redundant refresh when many parallel requests get 401 - we can
   * retry immediately with the fresh token instead of triggering another refresh.
   */
  wasRecentlyRefreshed(withinMs = 60 * 1000): boolean {
    if (this.lastRefreshAt <= 0) return false;
    return Date.now() - this.lastRefreshAt < withinMs;
  }

  triggerReauthForTesting() {
    this.triggerReauth('Manual reauthentication trigger');
  }

  private resetState() {
    this.expiresAt = null;
    this.lastRefreshAt = 0;
    this.reauthRequired = false;
    this.ongoingOperations = 0;
    this.clearReauthPromise();
  }

  private attachActivityListeners() {
    const handler = this.handleActivity;
    [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'wheel',
    ].forEach(eventType => {
      document.addEventListener(eventType, handler, { passive: true });
    });
  }

  private detachActivityListeners() {
    const handler = this.handleActivity;
    [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'wheel',
    ].forEach(eventType => {
      document.removeEventListener(eventType, handler);
    });
  }

  private handleActivity = () => {
    this.lastActivityAt = Date.now();
    if (!this.activityTimer) {
      this.activityTimer = window.setTimeout(() => {
        this.activityTimer = null;
        this.checkForRefresh();
      }, 1000);
    }
  };

  private checkForRefresh() {
    if (!this.isAuthenticated || this.reauthRequired) return;
    const now = Date.now();
    const recentlyActive = now - this.lastActivityAt < ACTIVE_WINDOW_MS;
    const hasOngoingOps = this.hasOngoingOperations();

    // Only refresh if user is active OR there are ongoing operations
    // This keeps session alive during uploads while still allowing expiry when truly idle
    if (!recentlyActive && !hasOngoingOps) return;

    // Periodic refresh every AUTO_REFRESH_INTERVAL_MS
    if (now - this.lastRefreshAt >= AUTO_REFRESH_INTERVAL_MS) {
      void this.refresh('periodic');
      return;
    }

    // Additionally, refresh early if token is about to expire
    if (this.expiresAt) {
      const remaining = this.expiresAt - now;
      if (remaining <= REFRESH_WINDOW_MS) {
        void this.refresh('expiry-window');
      }
    }
  }

  private async refresh(reason?: string) {
    if (this.refreshPromise) return this.refreshPromise;
    this.refreshPromise = this.performRefresh(reason).finally(() => {
      this.refreshPromise = null;
    });
    return this.refreshPromise;
  }

  private async performRefresh(reason?: string) {
    if (this.reauthRequired) {
      throw new Error('Session requires reauthentication');
    }

    if (this.tryAcquireLock()) {
      try {
        if (this.isDebug) {
          console.info('[SessionManager] Refresh start', { reason });
        }
        const response = await refreshToken();
        const expiresAt = this.extractExpiresAt(response?.token_exp);
        this.updateExpiry(expiresAt);
        this.emit('refresh-success', { expiresAt, reason });
        this.broadcast({ type: 'refresh-success', expiresAt });
        if (this.isDebug) {
          console.info('[SessionManager] Refresh success', { expiresAt });
        }
      } catch (error) {
        this.requireReauth(error instanceof Error ? error : new Error('Refresh failed'));
        if (this.isDebug) {
          console.warn('[SessionManager] Refresh failed', error);
        }
        throw new Error('Reauthentication required');
      } finally {
        this.releaseLock();
      }
      return;
    }

    await this.waitForRefreshResult();
  }

  private requireReauth(error: Error) {
    this.reauthRequired = true;
    if (!this.reauthPromise) {
      this.reauthPromise = new Promise<void>((resolve, reject) => {
        this.resolveReauth = resolve;
        this.rejectReauth = reject;
      });
    }
    this.emit('reauth-required', { error });
    this.broadcast({ type: 'reauth-required' });
    if (this.isDebug) {
      console.warn('[SessionManager] Reauth required', error.message);
    }
  }

  private waitForReauth() {
    if (!this.reauthPromise) {
      this.reauthPromise = new Promise<void>((resolve, reject) => {
        this.resolveReauth = resolve;
        this.rejectReauth = reject;
      });
    }
    return this.reauthPromise;
  }

  private clearReauthPromise() {
    this.reauthPromise = null;
    this.resolveReauth = null;
    this.rejectReauth = null;
  }

  private waitForRefreshResult() {
    return new Promise<void>((resolve, reject) => {
      const onSuccess = (event: Event) => {
        const detail = (event as CustomEvent).detail as { expiresAt?: number | null } | undefined;
        if (detail?.expiresAt) {
          this.updateExpiry(detail.expiresAt);
        }
        cleanup();
        resolve();
      };
      const onFailure = () => {
        cleanup();
        reject(new Error('Refresh failed in another tab'));
      };
      const onReauth = () => {
        cleanup();
        reject(new Error('Reauthentication required'));
      };

      const cleanup = () => {
        this.eventTarget.removeEventListener('refresh-success', onSuccess);
        this.eventTarget.removeEventListener('refresh-failure', onFailure);
        this.eventTarget.removeEventListener('reauth-required', onReauth);
        window.clearTimeout(timeoutId);
      };

      this.eventTarget.addEventListener('refresh-success', onSuccess);
      this.eventTarget.addEventListener('refresh-failure', onFailure);
      this.eventTarget.addEventListener('reauth-required', onReauth);

      const timeoutId = window.setTimeout(() => {
        cleanup();
        reject(new Error('Refresh timed out'));
      }, REFRESH_WAIT_TIMEOUT_MS);
    });
  }

  private updateExpiry(expiresAt: number | null) {
    this.expiresAt = expiresAt;
    this.lastRefreshAt = Date.now();
  }

  private extractExpiresAt(tokenExpSeconds?: number | null) {
    if (typeof tokenExpSeconds === 'number') {
      return tokenExpSeconds * 1000;
    }
    return Date.now() + FALLBACK_ACCESS_TTL_MS;
  }

  private emit(event: SessionEvent, detail?: unknown) {
    this.eventTarget.dispatchEvent(new CustomEvent(event, { detail }));
  }

  private broadcast(message: RefreshBroadcastMessage) {
    if (this.channel) {
      this.channel.postMessage({ ...message, owner: this.tabId });
    }
  }

  private setupBroadcastChannel() {
    if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
      return;
    }
    this.channel = new BroadcastChannel(CHANNEL_NAME);
    this.channel.onmessage = event => {
      const message = event.data as RefreshBroadcastMessage;
      if (!message?.type || message.owner === this.tabId) return;
      if (message.type === 'refresh-success') {
        if (typeof message.expiresAt === 'number') {
          this.updateExpiry(message.expiresAt);
        }
        this.emit('refresh-success', { expiresAt: message.expiresAt });
        return;
      }
      if (message.type === 'refresh-failure') {
        this.emit('refresh-failure');
        return;
      }
      if (message.type === 'reauth-required') {
        this.reauthRequired = true;
        this.emit('reauth-required');
        return;
      }
      if (message.type === 'reauth-success') {
        this.reauthRequired = false;
        this.emit('reauth-success');
        return;
      }
      if (message.type === 'reauth-cancelled') {
        this.reauthRequired = false;
        this.emit('reauth-cancelled');
      }
    };
  }

  private tryAcquireLock() {
    try {
      const now = Date.now();
      const raw = localStorage.getItem(LOCK_KEY);
      if (!raw) {
        localStorage.setItem(
          LOCK_KEY,
          JSON.stringify({ owner: this.tabId, expiresAt: now + LOCK_TTL_MS })
        );
        return true;
      }

      const parsed = JSON.parse(raw) as { owner?: string; expiresAt?: number };
      if (!parsed?.expiresAt || parsed.expiresAt < now || parsed.owner === this.tabId) {
        localStorage.setItem(
          LOCK_KEY,
          JSON.stringify({ owner: this.tabId, expiresAt: now + LOCK_TTL_MS })
        );
        return true;
      }
    } catch {
      // Best-effort: if storage fails, allow this tab to proceed.
      return true;
    }
    return false;
  }

  private releaseLock() {
    try {
      const raw = localStorage.getItem(LOCK_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as { owner?: string };
      if (parsed?.owner === this.tabId) {
        localStorage.removeItem(LOCK_KEY);
      }
    } catch {
      // Ignore lock release failures.
    }
  }

  private static generateTabId() {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
}

export const sessionManager = new SessionManager();

if (
  typeof window !== 'undefined' &&
  !(import.meta as unknown as { env?: { PROD?: boolean } }).env?.PROD
) {
  (window as unknown as { sessionManagerDebug?: unknown }).sessionManagerDebug = {
    triggerReauth: () => sessionManager.triggerReauthForTesting(),
  };
}
